import {
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  Globe2,
  LogOut,
  Menu,
  X,
  PanelLeft,
  PanelRight,
  Settings,
  Sun,
} from "lucide-react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, type CSSProperties, type MouseEvent } from "react";
import { useAuth } from "@/features/auth/AuthContext";
import { useTheme } from "@/features/theme/theme-context";
import { useWorkspace } from "@/features/workspaces/WorkspaceContext";
import { useI18n } from "@/i18n/I18nContext";
import { BrandLockup, BrandMark, Wordmark } from "@/shared/ui/brand";
import { Button } from "@/shared/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/shared/ui/sheet";

export function TemplateShell() {
  const [desktopCollapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMobileNavOpen(false), [location.pathname]);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const { locale, setLocale, t } = useI18n();
  const { workspaces, currentWorkspace, selectWorkspace } = useWorkspace();
  const meetingsActive = location.pathname.startsWith("/meetings") || location.pathname === "/";
  const displayName = user?.display_name || "Agung";
  const initials = displayName.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase() || "AG";
  const workspaceName = currentWorkspace?.name || "Personal";

  const themeLabels = { light: t("app.themeOptionLight"), dark: t("app.themeOptionDark"), system: t("app.themeOptionSystem") } as const;

  const closeNestedMenus = () => {
    setWorkspaceOpen(false);
    setLanguageOpen(false);
    setThemeOpen(false);
  };

  // A collapsed rail opens from a click anywhere on it. Links, buttons and the open
  // profile menu keep their own clicks; the toggle button is still the keyboard way.
  const expandFromRail = (event: MouseEvent<HTMLElement>) => {
    if (!desktopCollapsed) return;
    if ((event.target as HTMLElement).closest("a, button, [role='menu']")) return;
    setCollapsed(false);
    setProfileOpen(false);
    closeNestedMenus();
  };

  const renderSidebar = (mobile: boolean) => {
    const collapsed = !mobile && desktopCollapsed;
    const toggleLabel = mobile ? t("app.closeSidebar") : collapsed ? t("app.expandSidebar") : t("app.collapseSidebar");
    const toggleSidebar = () => {
      if (mobile) setMobileNavOpen(false); else setCollapsed((value) => !value);
      setProfileOpen(false);
      closeNestedMenus();
    };
    const sub = mobile ? "mt-1 w-full z-40" : "absolute left-[calc(100%+7px)] top-0 z-40 w-40";
    return <>
      {collapsed ? (
        // The mark is the rail's button: hovering or focusing it swaps in the expand icon.
        <div className="flex justify-center py-0.5">
          <Button type="button" variant="ghost" size="icon" onClick={toggleSidebar} className="group size-10 text-muted-foreground hover:bg-sidebar-accent hover:text-foreground [&_svg]:size-6" aria-label={toggleLabel}>
            <BrandMark size={24} className="transition-opacity group-hover:opacity-0 group-focus-visible:opacity-0" />
            <PanelRight strokeWidth={1.75} className="absolute opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 px-1 py-1.5">
          <Link to="/meetings" onClick={() => { if (mobile) setMobileNavOpen(false); }} className="rounded-md text-foreground no-underline" aria-label="Memoru">
            <BrandLockup markSize={24} wordmarkClassName="text-lg" />
          </Link>
          <Button type="button" variant="ghost" size="icon" onClick={toggleSidebar} className="size-7 shrink-0 text-muted-foreground hover:bg-sidebar-accent hover:text-foreground" aria-label={toggleLabel}>
            {mobile ? <X className="size-4" /> : <PanelLeft className="size-4" />}
          </Button>
        </div>
      )}

      <nav className="mt-5 grid gap-1" aria-label={t("app.primaryNavigation")}>
        <Link to="/meetings" className={`flex h-9 items-center rounded-md text-sm font-medium no-underline ${collapsed ? "justify-center px-0" : "px-2.5"} ${meetingsActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-sidebar-accent"}`} title={t("app.meetings")}>
          <CalendarDays className="size-[17px] shrink-0" />
          {!collapsed && <span className="ml-2.5">{t("app.meetings")}</span>}
        </Link>
      </nav>

      <div className="relative mt-auto border-t border-sidebar-border pt-3">
        <Button type="button" variant="ghost" onClick={() => { setProfileOpen((value) => !value); closeNestedMenus(); }} className={`h-auto w-full p-1.5 text-left hover:bg-sidebar-accent ${collapsed ? "justify-center" : "gap-2.5"}`} aria-expanded={profileOpen} aria-haspopup="menu">
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary/10 text-[13px] font-semibold text-primary">{initials}</span>
          {!collapsed && <><span className="min-w-0 flex-1"><span className="block truncate text-[13px] font-medium text-foreground">{displayName}</span><span className="block truncate text-xs text-muted-foreground">{workspaceName}</span></span><ChevronDown className={`size-3.5 text-muted-foreground transition-transform ${profileOpen ? "rotate-180" : ""}`} /></>}
        </Button>

        {profileOpen && <div className={`absolute bottom-[calc(100%+8px)] z-30 w-[250px] rounded-lg border border-border bg-card p-1.5 shadow-[0_8px_24px_rgba(24,24,27,0.16)] ${collapsed ? "left-[calc(100%+8px)]" : "left-0"}`} role="menu">
          <div className="relative px-2.5 pb-2.5">
            <p className="pb-2 pt-1 text-[11px] font-semibold uppercase tracking-[.04em] text-muted-foreground">{t("app.workspace")}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => { setWorkspaceOpen((value) => !value); setLanguageOpen(false); setThemeOpen(false); }} className="w-full justify-between gap-2 bg-card px-3 text-[13px]" aria-expanded={workspaceOpen}>
              <span className="truncate">{workspaceName}</span><ChevronDown className="size-4 shrink-0 text-muted-foreground" />
            </Button>
            {workspaceOpen && <div className="absolute left-2.5 right-2.5 top-full z-40 mt-1 rounded-lg border border-border bg-card p-1 shadow-lg">
              {(workspaces.length ? workspaces : [{ id: "personal", name: "Personal", role: "owner" as const }]).map((workspace) => {
                const active = currentWorkspace ? workspace.id === currentWorkspace.id : workspace.id === "personal";
                return <Button key={workspace.id} type="button" variant="ghost" onClick={() => { if (workspace.id !== "personal") selectWorkspace(workspace.id); setWorkspaceOpen(false); }} className="h-auto w-full justify-start gap-2 px-2 py-2 text-left text-[13px]"><span className="grid size-4 place-items-center text-primary">{active ? <Check className="size-3.5" /> : null}</span><span className="min-w-0 flex-1 truncate">{workspace.name}</span><span className="text-[11px] text-muted-foreground">{workspace.role}</span></Button>;
              })}
            </div>}
          </div>

          <div className="-mx-0.5 my-1 h-px bg-border" />
          <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-[.04em] text-muted-foreground">{t("app.interface")}</p>

          <div className="relative">
            <Button type="button" variant="ghost" onClick={() => { setLanguageOpen((value) => !value); setThemeOpen(false); setWorkspaceOpen(false); }} className="h-auto w-full justify-start gap-2 px-2.5 py-2 text-[13px]" aria-expanded={languageOpen}><Globe2 className="size-[15px]" /><span>{t("app.language")}</span><span className="ml-auto text-xs text-muted-foreground">{locale === "id" ? "Bahasa Indonesia" : "English"}</span><ChevronRight className="size-3.5 text-muted-foreground" /></Button>
            {languageOpen && <div className={`${sub} rounded-lg border border-border bg-card p-1.5 shadow-lg`}>{([['en', 'English'], ['id', 'Bahasa Indonesia']] as const).map(([value, label]) => <Button key={value} type="button" variant="ghost" onClick={() => { void setLocale(value).catch(() => undefined); setLanguageOpen(false); }} className="h-auto w-full justify-between px-2.5 py-2 text-[13px]"><span>{label}</span>{locale === value ? <Check className="size-3.5 text-primary" /> : null}</Button>)}</div>}
          </div>

          <div className="relative">
            <Button type="button" variant="ghost" onClick={() => { setThemeOpen((value) => !value); setLanguageOpen(false); setWorkspaceOpen(false); }} className="h-auto w-full justify-start gap-2 px-2.5 py-2 text-[13px]" aria-expanded={themeOpen}><Sun className="size-[15px]" /><span>{t("app.themeMenu")}</span><span className="ml-auto text-xs text-muted-foreground">{themeLabels[theme]}</span><ChevronRight className="size-3.5 text-muted-foreground" /></Button>
            {themeOpen && <div className={`${sub} rounded-lg border border-border bg-card p-1.5 shadow-lg`}>{(["light", "dark", "system"] as const).map((value) => <Button key={value} type="button" variant="ghost" onClick={() => { setTheme(value); setThemeOpen(false); }} className="h-auto w-full justify-between px-2.5 py-2 text-[13px]"><span>{themeLabels[value]}</span>{theme === value ? <Check className="size-3.5 text-primary" /> : null}</Button>)}</div>}
          </div>

          <div className="-mx-0.5 my-1 h-px bg-border" />
          <Link to="/settings" onClick={() => setProfileOpen(false)} className="flex items-center gap-2 rounded-md px-2.5 py-2 text-[13px] text-foreground no-underline hover:bg-muted"><Settings className="size-[15px]" />{t("app.settings")}</Link>
          <div className="-mx-0.5 my-1 h-px bg-border" />
          <Button type="button" variant="ghost" onClick={() => void signOut().finally(() => navigate("/login"))} className="h-auto w-full justify-start gap-2 px-2.5 py-2 text-[13px]"><LogOut className="size-[15px]" />{t("app.logOut")}</Button>
        </div>}
      </div>
    </>;
  };

  return <div className="min-h-dvh bg-background text-foreground md:grid md:grid-cols-[var(--template-sidebar)_1fr]" style={{ "--template-sidebar": desktopCollapsed ? "4.5rem" : "15rem" } as CSSProperties}>
    <aside onClick={expandFromRail} className={`hidden border-r border-sidebar-border bg-sidebar p-3 md:flex md:min-h-dvh md:flex-col ${desktopCollapsed ? "cursor-e-resize" : ""}`}>
      {renderSidebar(false)}
    </aside>

    <header className="flex h-14 items-center gap-2 border-b border-border px-4 md:hidden">
      <Button type="button" variant="ghost" size="icon" onClick={() => setMobileNavOpen(true)} className="-ml-2 size-9 text-foreground" aria-label={t("app.openNavigation")}><Menu className="size-5" /></Button>
      <Link to="/meetings" className="text-lg text-foreground no-underline" aria-label="Memoru"><Wordmark /></Link>
    </header>
    <Sheet open={mobileNavOpen} onOpenChange={(open) => { setMobileNavOpen(open); if (!open) { setProfileOpen(false); closeNestedMenus(); } }}>
      <SheetContent side="left" className="max-w-[280px] border-sidebar-border bg-sidebar p-3 md:hidden">
        <SheetTitle className="sr-only">Memoru</SheetTitle>
        <SheetDescription className="sr-only">{t("app.navigationSidebar")}</SheetDescription>
        {renderSidebar(true)}
      </SheetContent>
    </Sheet>
    <main className="min-w-0"><Outlet /></main>
  </div>;
}
